import type { IAuthManagerInstantiator } from '../../controller/interface/auth';
import type { IMediaManagerInstantiator } from '../../controller/interface/media';
import type { IUserStateInstantiator } from '../../controller/interface/userstate';
import type { IWorkspaceDBInstantiator } from '../../db/interface';
import type { IConfigAPIRequired } from '../../interface';
import type { ISession, ISessionArgs } from '../interface/session';
import sessionDB, { ISessionData } from '../private/db';
import sessionSerialize from './session.serialize';

const _remove = async (session: ISession) => {
    const hasSession = await sessionDB.exists(session.name);

    if (!hasSession) {
        throw new Error(`Session ${session.name}, does not exist, cannot remove`);
    }

    await sessionDB.remove(session.name);
};

const _save = async (session: ISession) => {
    await sessionDB.save(session.name, sessionSerialize.flatten(session));
};

function buildFromSessionData(
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    data: ISessionData,
): Promise<ISession> {
    return new Promise(async function (resolve, reject) {
        try {
            const authArgs = authInst.expand(data.auth);
            const auth = authInst.open(authArgs);

            const db = await dbInst.open({ name: data.name });

            const media = mediaInst.open(db, auth);
            const state = userInst.expand(data.userState);

            const session: ISession = {
                name: data.name,

                auth,
                db,
                media,
                state,

                save: async function (this: ISession) {
                    return _save(this);
                },
                remove: async function (this: ISession) {
                    return _remove(this);
                },
            };

            resolve(session);
        } catch (error) {
            reject(error);
        }
    });
}

function buildFromArgs(
    configAPI: IConfigAPIRequired,
    authInst: IAuthManagerInstantiator,
    mediaInst: IMediaManagerInstantiator,
    userInst: IUserStateInstantiator,
    dbInst: IWorkspaceDBInstantiator,
    nameIn: string,
    args: ISessionArgs,
): Promise<ISession> {
    return new Promise(async function (resolve, reject) {
        try {
            const name = nameIn ?? args.apiUsername ?? configAPI.auth.username;

            const auth = authInst.open({
                apiUrl: args.apiUrl ?? configAPI.url,
                apiExtension: configAPI.extension.auth,
                clientId: configAPI.auth.clientId,
                clientSecret: configAPI.auth.clientSecret,
                grantType: configAPI.auth.grantType,
                username: args.apiUsername ?? configAPI.auth.username,
                password: args.apiPassword ?? configAPI.auth.password,
            });

            const db = await dbInst.open({ name });

            const media = mediaInst.open(db, auth);
            const state = userInst.open(name);

            const session: ISession = {
                name,

                auth,
                db,
                media,
                state,

                save: async function (this: ISession) {
                    return _save(this);
                },
                remove: async function (this: ISession) {
                    return _remove(this);
                },
            };

            resolve(session);
        } catch (error) {
            reject(error);
        }
    });
}

const module = {
    buildFromArgs,
    buildFromSessionData,
};

export default module;
